import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Receipt } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.name}`);
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-slate-50">
      {/* Left – image / brand */}
      <div
        className="hidden lg:flex relative bg-slate-900 overflow-hidden"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/17692430/pexels-photo-17692430.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="relative z-10 p-12 flex flex-col justify-between text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white flex items-center justify-center">
              <Receipt className="w-5 h-5 text-slate-900" />
            </div>
            <span className="font-display text-xl font-semibold tracking-tight">
              BILLING
            </span>
          </div>
          <div>
            <p className="label-tag text-slate-200 mb-4">Professional Billing</p>
            <h2 className="font-display text-4xl xl:text-5xl font-light leading-tight max-w-md">
              Clean invoices. Clear payments. Zero clutter.
            </h2>
          </div>
        </div>
      </div>

      {/* Right – form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <Card className="w-full max-w-md border-slate-200 shadow-none">
          <CardContent className="p-8">
            <div className="mb-8">
              <p className="label-tag mb-2">Sign in</p>
              <h1 className="font-display text-3xl font-medium text-slate-900">
                Welcome back
              </h1>
              <p className="text-sm text-slate-500 mt-2">
                Access your billing dashboard.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="label-tag">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="rounded-md border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="label-tag">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="rounded-md border-slate-300"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                data-testid="login-submit-btn"
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-md h-11"
              >
                {submitting ? "Signing in…" : "Sign In"}
              </Button>
            </form>

            <p className="mt-6 text-sm text-slate-500 text-center">
              Don't have an account?{" "}
              <Link to="/register" className="text-slate-900 font-medium underline underline-offset-4" data-testid="go-register-link">
                Create one
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
